import {
  BriefcaseActions,
  BriefcaseItem,
  SearchItem,
  VignetteHelpers,
  VignetteRoutes,
} from "@/types";
import { useAppStore } from "@/pinia/app";
import { useBriefcaseStore } from "@/pinia/briefcase";
import { useSessionStore } from "@/pinia/session";
import { computed, ref, toRefs } from "vue";
import { useRouter } from "vue-router/composables";
import { ApiService, SnackbarService } from "@/services";

interface UseProfileParams {
  briefcaseView: boolean;
  searchItem: SearchItem | BriefcaseItem;
  displaySummary: boolean;
  selectionEnabled: boolean;
  /**
   * -1: off
   * 0: on, none selected
   * 1: on and selected
   */
  selectionMode: number;
  /**
   * Represents section title from which ProfileCard was clicked
   */
  source: string;
  emit: (event: "profile-selected", ...payload: unknown[]) => void;
  forceUpdate: () => void;
}

export function useProfile(options: UseProfileParams) {
  const {
    briefcaseView,
    displaySummary,
    selectionEnabled,
    selectionMode,
    source,
    searchItem,
  } = toRefs(options);

  const router = useRouter();

  const briefcaseStore = useBriefcaseStore();
  const { isInBriefcase } = briefcaseStore;
  const isAddedToBriefcase = isInBriefcase(options.searchItem.briefcaseId);

  const sessionStore = useSessionStore();
  const isWebUser = computed(() => sessionStore.isWebUser);

  const appStore = useAppStore();

  const expanded = ref(false);

  const isSearchItem = (
    item: SearchItem | BriefcaseItem
  ): item is SearchItem => {
    return (item as SearchItem).type !== undefined;
  };

  const profileRoute = computed(() => {
    let route = VignetteRoutes.Profile;
    if (searchItem.value && isSearchItem(searchItem.value)) {
      route = VignetteHelpers.getRouteFromSearchItemType(
        searchItem.value.type ?? ""
      );
    }
    return router.resolve({
      name: route,
      params: {
        id: String(searchItem.value.id),
        name: VignetteHelpers.getProfileNameSlug(
          searchItem.value.preferredNameDisplay
        ),
      },
    });
  });

  const toggleBriefcase = async () => {
    if (isAddedToBriefcase.value) {
      const folders = briefcaseStore.getFolderIdsById(
        searchItem.value.briefcaseId
      ).value;

      if (folders.length > 1) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        briefcaseStore.meta.itemBeingMoved = searchItem.value;
      } else {
        await ApiService.postBriefcaseToggle(
          [searchItem.value],
          BriefcaseActions.REMOVE
        );
      }

      VignetteHelpers.Analytics.push({
        ["event"]: "briefcase-remove-items",
        ["custom.gtm.itemId"]: searchItem.value.id,
        ["custom.gtm.itemType"]: "Profile",
      });
      SnackbarService.displaySnackbar(
        `${searchItem.value.preferredNameDisplay} removed from briefcase`,
        "primary"
      );
      options.emit("profile-selected", searchItem.value, false);
    } else {
      const result = await ApiService.postBriefcaseToggle(
        [searchItem.value],
        BriefcaseActions.ADD
      );
      if (result) {
        VignetteHelpers.Analytics.push({
          ["event"]: "briefcase-add-items",
          ["custom.gtm.itemId"]: searchItem.value.id,
          ["custom.gtm.itemType"]: "Profile",
        });

        SnackbarService.displaySnackbar(
          `${searchItem.value.preferredNameDisplay} added to briefcase`,
          "primary",
          3000,
          searchItem.value
        );
        options.emit("profile-selected", searchItem.value, true);
      } else {
        SnackbarService.displaySnackbar(
          `You have too many profiles in your briefcase, try moving them around.`,
          "error",
          -1,
          searchItem.value
        );
      }
    }

    options.forceUpdate();
  };

  const removeFromBriefcaseFolder = async (): Promise<void> => {
    const currentFolderId = briefcaseStore.meta.currentFolderId;
    await ApiService.postBriefcaseToggle(
      [searchItem.value],
      BriefcaseActions.REMOVE,
      currentFolderId
    );

    VignetteHelpers.Analytics.push({
      ["event"]: "briefcase-remove-items",
      ["custom.gtm.itemId"]: searchItem.value.id,
      ["custom.gtm.itemType"]: "Profile",
    });

    SnackbarService.displaySnackbar(
      `${searchItem.value.preferredNameDisplay} removed from Briefcase.`,
      "primary"
    );

    // Changes aren't picked up on SearchResults.vue....
    options.forceUpdate();
  };

  const toggle = (): void => {
    options.emit(
      "profile-selected",
      searchItem.value,
      selectionMode.value <= 0
    );
  };

  const profileClickSelection = computed<string | null>(() => {
    return selectionMode.value >= 0 ? "click" : null;
  });

  const profileClickEnabled = computed<string | null>(() => {
    return selectionMode.value < 0 ? "click" : null;
  });

  const sendClickEvent = (): void => {
    if (source.value !== "") {
      VignetteHelpers.Analytics.push({
        ["event"]: "vignette-member-homepage-click",
        ["custom.gtm.elementText"]: source.value,
        ["custom.gtm.elementUrl"]: `${
          appStore.baseUrlOrigin + profileRoute.value.route.path.substring(1)
        }`,
      });
    }
    if (selectionMode.value < 0) {
      router.push(profileRoute.value.location);
    }
  };

  return {
    selectionEnabled,
    briefcaseView,
    displaySummary,
    selectionMode,

    expanded,
    isWebUser,
    profileRoute,
    removeFromBriefcaseFolder,
    sendClickEvent,
    toggle,
    toggleBriefcase,
    profileClickSelection,
    profileClickEnabled,
  };
}
